import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, ListOrdered, DollarSign, Printer as PrinterIcon, QrCode, CreditCard, Settings, LogOut, Printer, AlertTriangle, TicketPercent, Bell, Menu, X } from "lucide-react";
import PendingApprovalWatcher from "@/pages/store/PendingApprovalWatcher";

const items = [
  { to: "/store", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "nav-store-dashboard" },
  { to: "/store/jobs", icon: ListOrdered, label: "Print Jobs", testid: "nav-store-jobs" },
  { to: "/store/notifications", icon: Bell, label: "Notifications", testid: "nav-store-notifications" },
  { to: "/store/coupons", icon: TicketPercent, label: "Coupons", testid: "nav-store-coupons" },
  { to: "/store/pricing", icon: DollarSign, label: "Pricing", testid: "nav-store-pricing" },
  { to: "/store/printer", icon: PrinterIcon, label: "Printer", testid: "nav-store-printer" },
  { to: "/store/public-url", icon: QrCode, label: "Public URL", testid: "nav-store-public" },
  { to: "/store/subscription", icon: CreditCard, label: "Subscription", testid: "nav-store-sub" },
  { to: "/store/settings", icon: Settings, label: "Settings", testid: "nav-store-settings" },
];

export default function StoreLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [store, setStore] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/store/me").then((r) => setStore(r.data)); }, []);

  return (
    <div className="min-h-screen dashboard-bg lg:flex">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 h-14 px-4 flex items-center justify-between">
        <button data-testid="store-menu-open" onClick={() => setOpen(true)} className="p-2 -ml-2 rounded-lg hover:bg-slate-100"><Menu className="w-5 h-5" /></button>
        <div className="flex items-center gap-2">
          {store?.logo_url
            ? <img src={store.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover" onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "grid"; }} />
            : null}
          <div className={store?.logo_url ? "w-8 h-8 rounded-lg place-items-center hidden" : "w-8 h-8 grid place-items-center"}><img src="/logo.png" alt="Print Plus" className="w-8 h-8 object-contain" /></div>
          <div className="font-display font-bold truncate max-w-[180px] text-sm">{store?.name || "Store"}</div>
        </div>
        <div className="w-9" />
      </div>

      {/* Backdrop */}
      {open && <div onClick={() => setOpen(false)} className="lg:hidden fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm animate-in fade-in" />}

      {/* Sidebar (drawer on mobile, static on desktop) */}
      <aside className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-72 lg:w-64 border-r border-slate-200 bg-white flex flex-col shadow-xl lg:shadow-none transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 border-b border-slate-200 flex items-center gap-2">
          {store?.logo_url
            ? <img src={store.logo_url} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "grid"; }} data-testid="sidebar-logo" />
            : null}
          <div className={store?.logo_url ? "w-9 h-9 rounded-lg place-items-center flex-shrink-0 hidden" : "w-9 h-9 grid place-items-center flex-shrink-0"}><img src="/logo.png" alt="Print Plus" className="w-9 h-9 object-contain" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold truncate">{store?.name || "Store"}</div>
            <div className="text-[10px] uppercase tracking-widest text-orange-500 font-semibold">Store Owner</div>
          </div>
          <button data-testid="store-menu-close" onClick={() => setOpen(false)} className="lg:hidden p-2 -mr-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.testid} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${isActive ? "bg-orange-50 text-orange-700" : "text-slate-600 hover:bg-slate-50 active:bg-slate-100"}`}>
              <it.icon className="w-4 h-4" />{it.label}
            </NavLink>
          ))}
        </nav>
        {store && !store.subscription_active && (
          <div className="mx-3 mb-2 p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
            <AlertTriangle className="w-4 h-4 mb-1" />Subscription expired. Renew to accept orders.
          </div>
        )}
        <div className="p-3 border-t border-slate-200">
          <div className="px-3 py-2 text-xs text-slate-500 truncate">{user?.email}</div>
          <Button variant="ghost" size="sm" className="w-full justify-start" onClick={async () => { await logout(); nav("/login"); }} data-testid="store-logout">
            <LogOut className="w-4 h-4 mr-2" />Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden"><Outlet /></main>
      <PendingApprovalWatcher />
    </div>
  );
}
